import React, { useEffect, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Input,
  Label,
  Form,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  FormFeedback,
  Container,
  Alert,
  Button,
} from "reactstrap";
import { FormikProvider, useFormik } from "formik";
import schema from "./schema";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import BoqItemsFieldArray from "components/common/BoqItemsFieldArray";

import { createAmc, getAmc, updateAmc } from "features/Amc/services/AmcService";
import type { Amc } from "features/Amc/types/amc.types";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import { getClientOptions } from "features/Clients/services/ClientService";
import { getQuotation } from "features/Quotations/services/QuotationService";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import {
  getChecklistTemplateById,
  // getChecklistTemplateOptions,
} from "features/ChecklistTemplates/services/ChecklistTemplateService";
import {
  AmcContractStatus,
  AmcSourceType,
  amcStatusMap,
} from "features/Amc/constants/amc-contract.constants";
import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import { WorkOrderItemSource } from "features/WorkOrders/constants/work-order-item.constants";
import {
  amcItemToBoqRow,
  boqRowToAmcPersistLine,
  type AmcBoqFormRow,
} from "features/Amc/utils/amcItems";
import type {
  AddAmcRequest,
  UpdateAmcRequest,
} from "features/Amc/types/amc.types";
import { getDocumentTitle } from "utils/helpers";
import StatusBadge from "components/common/StatusBadge";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import { ClientAdvanceSourceType } from "features/ClientAdvances/constants/client-advance.constants";

function isPlaceholderAmcBoqRow(row: unknown): boolean {
  if (!row || typeof row !== "object") return true;
  const r = row as AmcBoqFormRow;
  const desc = String(r.description ?? "").trim();
  const unit = String(r.unit ?? "").trim();
  const pid = r.productId;
  const hasProduct =
    pid !== "" &&
    pid != null &&
    Number.isFinite(Number(pid)) &&
    Number(pid) > 0;
  const qty = Number(r.quantity);
  const rate = Number(r.rate);
  return (
    !hasProduct &&
    desc === "" &&
    unit === "" &&
    r.type === QuotationItemType.SERVICE &&
    (Number.isFinite(qty) ? qty === 1 : true) &&
    (Number.isFinite(rate) ? rate === 0 : true)
  );
}

const ContractForm = ({ isEdit = false }: { isEdit?: boolean }) => {
  document.title = getDocumentTitle(!isEdit ? "New contract" : "Edit contract");

  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState<Amc>();
  const [quotationTotal, setQuotationTotal] = useState<number | null>(null);
  // const [checklistTemplateDefaultOption, setChecklistTemplateDefaultOption] =
  //   useState<SelectOption | undefined>();

  const emptyAmcBoqRow = (): AmcBoqFormRow => ({
    type: QuotationItemType.SERVICE,
    description: "",
    productId: "",
    lineId: undefined,
    onHandQty: undefined,
    quantity: 1,
    unit: "",
    rate: 0,
    sourceType: WorkOrderItemSource.ADHOC,
    quotationItemId: null,
    originalQuantity: null,
  });

  const isDraft = !record || record.status === AmcContractStatus.DRAFT;
  const isActive = !!record && record.status === AmcContractStatus.ACTIVE;
  const isCancelled = !!record && record.status === AmcContractStatus.CANCELLED;
  const canSubmit =
    !record ||
    record.status === AmcContractStatus.DRAFT ||
    record.status === AmcContractStatus.ACTIVE;
  const visitsMin = 1;
  /** Non-draft contracts cannot change scheduled visit count (extras are added from the contract overview). */
  const visitsDisabled = isCancelled || !isDraft;
  const datesDisabled = !isDraft;
  const headerFieldsDisabled = !isDraft;

  const lockedFormAlertMessage =
    record == null
      ? null
      : record.status === AmcContractStatus.CANCELLED
        ? "This contract has been cancelled and can no longer be edited."
        : record.status === AmcContractStatus.ACTIVE
          ? "Active contracts cannot change the scheduled visit count here — add extra visits from the contract overview. Dates, billing type, and line items are locked."
          : record.status !== AmcContractStatus.DRAFT
            ? "Only draft contracts can be edited from this form."
            : null;

  const boqReadOnly =
    !isDraft ||
    (!!record &&
      record.sourceType === AmcSourceType.QUOTATION &&
      (record.items?.length ?? 0) > 0);

  const isReadyForActivationStatus =
    !!record && record.status === AmcContractStatus.READY_FOR_ACTIVATION;
  /** Editable only while DRAFT or READY_FOR_ACTIVATION — same window as Title. */
  const agreedAmountLocked =
    isEdit && !!record && !isDraft && !isReadyForActivationStatus;

  const validation: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: record?.code ?? "",
      title: record?.title ?? "",
      clientId: record?.clientId ?? "",
      startDate: record?.startDate ?? "",
      endDate: record?.endDate ?? "",
      numberOfVisits: record?.numberOfVisits ?? "",
      checklistTemplateId:
        record?.checklistTemplateId != null &&
        Number(record.checklistTemplateId) > 0
          ? record.checklistTemplateId
          : "",
      items: record?.items?.length
        ? record.items.map((ln) => amcItemToBoqRow(ln))
        : [emptyAmcBoqRow()],
      agreedAmount:
        record?.agreedAmount ?? (quotationTotal != null ? quotationTotal : ""),
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      if (!canSubmit) {
        toast.error("This contract cannot be edited in its current status.");
        return;
      }

      const rows: AmcBoqFormRow[] = Array.isArray(values.items)
        ? values.items
        : [];
      const meaningfulRows = rows.filter((r) => !isPlaceholderAmcBoqRow(r));
      const itemsPayload = meaningfulRows.map((r) => boqRowToAmcPersistLine(r));

      const draftStatus = record?.status === AmcContractStatus.DRAFT;

      const ctRaw = values.checklistTemplateId;
      const checklistTemplateIdResolved =
        ctRaw !== "" &&
        ctRaw != null &&
        Number.isFinite(Number(ctRaw)) &&
        Number(ctRaw) > 0
          ? Number(ctRaw)
          : undefined;

      const codeValue = values.code?.trim() || undefined;

      const header = {
        ...(codeValue !== undefined ? { code: codeValue } : {}),
        title: values.title?.trim() || null,
        clientId: Number(values.clientId),
        startDate: values.startDate,
        endDate: values.endDate,
        numberOfVisits:
          !isEdit || draftStatus || !isActive
            ? Number(values.numberOfVisits)
            : Number(record?.numberOfVisits ?? values.numberOfVisits),
        ...(draftStatus || !isEdit
          ? {
              ...(checklistTemplateIdResolved != null
                ? { checklistTemplateId: checklistTemplateIdResolved }
                : {}),
            }
          : {}),
        ...(!agreedAmountLocked
          ? {
              agreedAmount:
                values.agreedAmount === "" || values.agreedAmount == null
                  ? null
                  : Number(values.agreedAmount),
            }
          : {}),
      };

      const contractBase: UpdateAmcRequest = {
        ...header,
      };

      const createPayload: AddAmcRequest = {
        ...contractBase,
        ...(itemsPayload.length > 0 ? { items: itemsPayload } : {}),
      };

      const includeItemsInUpdate =
        !!isEdit &&
        !!id &&
        draftStatus &&
        !boqReadOnly &&
        (itemsPayload.length > 0 || Boolean(record?.items?.length));

      const updateFullPayload: UpdateAmcRequest = {
        ...contractBase,
        ...(includeItemsInUpdate
          ? { items: itemsPayload.length > 0 ? itemsPayload : [] }
          : {}),
      };

      let response;
      try {
        if (isEdit && id) {
          const payload: UpdateAmcRequest =
            draftStatus && !boqReadOnly ? updateFullPayload : contractBase;
          response = await updateAmc(Number(id), payload);
        } else {
          response = await createAmc(createPayload);
        }

        if (response.success) {
          toast.success(response.message);
          navigate(-1);
        } else {
          toast.error(response.message);
        }
      } catch (err) {
        toast.error((err as any).response.data.message);
      }
    },
  });

  const endDateFlatpickrOptions = {
    altInput: true,
    altFormat: "d M, Y",
    dateFormat: "d M, Y",
  };

  useEffect(() => {
    if (!isEdit || !id) return;
    const numericId = Number(id);
    if (!Number.isInteger(numericId) || numericId <= 0) return;

    const fetchRecord = async () => {
      try {
        const response = await getAmc(numericId);
        const { success, data } = response;
        if (success && data) {
          setRecord(data);
        }
      } catch (err) {
        console.warn(err);
      }
    };

    void fetchRecord();
  }, [id, isEdit]);

  useEffect(() => {
    if (
      record?.sourceType !== AmcSourceType.QUOTATION ||
      record?.agreedAmount != null
    ) {
      return;
    }
    const quotationId = Number(record.quotationId ?? record.source?.id);
    if (!Number.isFinite(quotationId) || quotationId <= 0) return;

    let cancelled = false;
    void (async () => {
      try {
        const response = await getQuotation(quotationId);
        if (!cancelled && response.success && response.data) {
          setQuotationTotal(response.data.total ?? null);
        }
      } catch (err) {
        console.warn(err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    record?.sourceType,
    record?.quotationId,
    record?.source?.id,
    record?.agreedAmount,
  ]);

  useEffect(() => {
    const ctId = Number(record?.checklistTemplateId);
    if (!Number.isInteger(ctId) || ctId <= 0) {
      // setChecklistTemplateDefaultOption(undefined);
      return;
    }
    let cancelled = false;
    void getChecklistTemplateById(ctId)
      .then((res) => {
        if (cancelled || !res.success || !res.data) return;
        // setChecklistTemplateDefaultOption({
        //   value: res.data.id,
        //   label: res.data.name?.trim() || `Template #${res.data.id}`,
        // });
      })
      .catch(() => {
        // if (!cancelled) setChecklistTemplateDefaultOption(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [record?.checklistTemplateId]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit contract" : "New contract"}
            pageTitle="AMC Management"
            items={[
              {
                label: "Contracts",
                path: "/amc-management",
              },
            ]}
          />
          <FormikProvider value={validation}>
            <Form
              className="needs-validation"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Row className="justify-content-center">
                <Col>
                  <Card className="shadow-none border">
                    <CardHeader className="border-bottom bg-transparent pb-3">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h5 className="card-title mb-0">
                          {isEdit ? "Edit contract" : "Create contract"}
                        </h5>
                        {record ? (
                          <StatusBadge
                            value={record.status}
                            map={amcStatusMap}
                          />
                        ) : null}
                      </div>
                      <p className="text-muted mb-0 small">
                        Choose the client and optional primary owner, then set
                        the contract value, active period, and how many service
                        visits apply per year.
                      </p>
                    </CardHeader>
                    <CardBody className="pt-4">
                      {lockedFormAlertMessage ? (
                        <Alert color="warning" className="mb-4" role="alert">
                          <i className="ri-information-line me-2 align-middle" />
                          {lockedFormAlertMessage}
                        </Alert>
                      ) : null}

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Contract
                      </div>
                      <Row className="g-3 mb-4">
                        <Col md={4}>
                          <Label className="form-label" for="amc-code">
                            Code{" "}
                            <span className="text-muted fw-normal">
                              (optional)
                            </span>
                          </Label>
                          <Input
                            id="amc-code"
                            type="text"
                            name="code"
                            placeholder="Auto-generated if blank"
                            value={validation.values.code || ""}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            readOnly={isEdit && !isDraft}
                          />
                          {isEdit && !isDraft ? (
                            <p className="text-muted small mb-0 mt-1">
                              Code cannot be changed after approval.
                            </p>
                          ) : null}
                        </Col>
                        <Col md={12}>
                          <Label className="form-label" for="amc-title">
                            Title
                          </Label>
                          <Input
                            id="amc-title"
                            type="text"
                            name="title"
                            placeholder="e.g. Annual HVAC maintenance — Building A"
                            value={validation.values.title || ""}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.title &&
                                validation.errors.title
                              )
                            }
                          />
                          {validation.touched.title &&
                          validation.errors.title ? (
                            <FormFeedback type="invalid">
                              {String(validation.errors.title)}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Client
                      </div>
                      <Row className="mb-2">
                        <Col md={6}>
                          <div className="d-flex flex-column gap-3">
                            <div>
                              <Label className="form-label">Client</Label>
                              <AsyncPaginatedSelect
                                defaultOption={
                                  record?.client && {
                                    value: record.client.id,
                                    label:
                                      record.client.name?.trim() ||
                                      record.client.company?.trim() ||
                                      `Client #${record.client.id}`,
                                  }
                                }
                                fetchOptions={getClientOptions}
                                placeholder="Search clients…"
                                value={validation.values.clientId}
                                onChange={(val: any) =>
                                  validation.setFieldValue(
                                    "clientId",
                                    val?.value,
                                  )
                                }
                                onBlur={() =>
                                  validation.setFieldTouched("clientId", true)
                                }
                                isInvalid={
                                  validation.touched.clientId &&
                                  validation.errors.clientId
                                }
                                isDisabled={headerFieldsDisabled}
                              />
                              {validation.touched.clientId &&
                              validation.errors.clientId ? (
                                <FormFeedback
                                  type="invalid"
                                  className="d-block"
                                >
                                  <div>{validation.errors.clientId}</div>
                                </FormFeedback>
                              ) : null}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Visits
                      </div>
                      <Row className="g-3 mb-2">
                        <Col lg={6}>
                          <Label
                            className="form-label"
                            for="amc-number-of-visits"
                          >
                            Number of visits
                          </Label>
                          <Input
                            id="amc-number-of-visits"
                            type="number"
                            min={visitsMin}
                            max={12}
                            className="form-control"
                            placeholder="e.g. 4"
                            name="numberOfVisits"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.numberOfVisits || ""}
                            invalid={
                              validation.touched.numberOfVisits &&
                              Boolean(validation.errors.numberOfVisits)
                            }
                            disabled={visitsDisabled}
                          />
                          {(validation.touched.numberOfVisits ||
                            validation.submitCount > 0) &&
                          validation.errors.numberOfVisits ? (
                            <FormFeedback type="invalid">
                              {validation.errors.numberOfVisits}
                            </FormFeedback>
                          ) : null}
                          <p className="text-muted small mb-0 mt-1">
                            {isDraft
                              ? "Scheduled visits per year (1–12)."
                              : "Locked after approval — use extra visits on the contract overview."}
                          </p>
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Schedule
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label className="form-label" for="amc-start-date">
                            Start date
                          </Label>
                          <Flatpickr
                            id="amc-start-date"
                            name="startDate"
                            className={`form-control ${
                              validation.errors.startDate &&
                              validation.touched.startDate
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Select start date"
                            options={{
                              altInput: true,
                              altFormat: "d M, Y",
                              dateFormat: "d M, Y",
                            }}
                            onChange={(date: any) =>
                              validation.setFieldValue(
                                "startDate",
                                moment(date[0]).format("DD MMMM ,YYYY"),
                              )
                            }
                            value={
                              validation.values.startDate
                                ? new Date(validation.values.startDate)
                                : ""
                            }
                            disabled={datesDisabled}
                          />
                          {validation.touched.startDate &&
                          validation.errors.startDate ? (
                            <FormFeedback type="invalid">
                              {validation.errors.startDate}
                            </FormFeedback>
                          ) : null}
                        </Col>
                        <Col md={6}>
                          <Label className="form-label" for="amc-end-date">
                            End date
                          </Label>
                          <Flatpickr
                            id="amc-end-date"
                            name="endDate"
                            className={`form-control ${
                              validation.errors.endDate &&
                              validation.touched.endDate
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Select end date"
                            options={endDateFlatpickrOptions}
                            onChange={(date: any) =>
                              validation.setFieldValue(
                                "endDate",
                                moment(date[0]).format("DD MMMM ,YYYY"),
                              )
                            }
                            value={
                              validation.values.endDate
                                ? new Date(validation.values.endDate)
                                : ""
                            }
                            disabled={datesDisabled}
                          />
                          {validation.touched.endDate &&
                          validation.errors.endDate ? (
                            <FormFeedback type="invalid">
                              {validation.errors.endDate}
                            </FormFeedback>
                          ) : null}
                          <p className="text-muted small mb-0 mt-1">
                            Must be exactly 1, 2, 3… full years after the start
                            date.
                          </p>
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                        Financials
                      </div>
                      <Row className="g-3 mb-2">
                        <Col md={6}>
                          <Label className="form-label" for="amc-agreed-amount">
                            Agreed amount
                          </Label>
                          <div className="input-group">
                            <span className="input-group-text text-muted">
                              <i className="ri-shake-hands-line" />
                            </span>
                            <Input
                              id="amc-agreed-amount"
                              type="number"
                              min={0}
                              step="0.01"
                              name="agreedAmount"
                              placeholder="0.00"
                              readOnly={agreedAmountLocked}
                              value={validation.values.agreedAmount ?? ""}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                !agreedAmountLocked &&
                                !!(
                                  validation.touched.agreedAmount &&
                                  validation.errors.agreedAmount
                                )
                              }
                            />
                            {!agreedAmountLocked &&
                            validation.touched.agreedAmount &&
                            validation.errors.agreedAmount ? (
                              <FormFeedback>
                                {validation.errors.agreedAmount}
                              </FormFeedback>
                            ) : null}
                          </div>
                          <small className="text-muted">
                            {agreedAmountLocked
                              ? "Locked once the contract is active."
                              : "Independent of line items — set the contract's value directly."}
                            {quotationTotal != null &&
                            validation.values.agreedAmount === "" ? (
                              <>
                                {" "}
                                — quotation total:{" "}
                                <span className="text-body fw-medium">
                                  {formatCurrency(quotationTotal)}
                                </span>
                              </>
                            ) : null}
                            {record?.finalAmount != null ? (
                              <>
                                {" "}
                                — final amount:{" "}
                                <span className="text-body fw-medium">
                                  {formatCurrency(record.finalAmount)}
                                </span>
                              </>
                            ) : null}
                          </small>
                          {!agreedAmountLocked ? (
                            <small className="text-muted d-block mt-1">
                              Reducing this value below the total of any active
                              client advances already collected against this
                              contract will be blocked.
                            </small>
                          ) : null}
                          {isEdit && record ? (
                            <LinkedClientAdvancesTable
                              className="mt-3"
                              sourceType={ClientAdvanceSourceType.AMC}
                              sourceId={record.id}
                            />
                          ) : null}
                        </Col>
                      </Row>

                      <hr className="my-4 text-muted opacity-25" />

                      <div className="text-uppercase text-muted fw-semibold fs-12 mb-2">
                        Contract line items (optional)
                      </div>
                      <p className="text-muted small mb-3">
                        {boqReadOnly
                          ? "These lines were generated from the accepted quotation — invoice-breakdown detail only, independent of the agreed amount above."
                          : "Optional breakdown (material / labour / service) for invoicing detail — independent of the agreed amount above."}
                      </p>
                      <div className="border rounded p-3 bg-light bg-opacity-25">
                        <BoqItemsFieldArray
                          validation={validation}
                          readOnly={boqReadOnly}
                          addLineLabel="Add line item"
                          getDefaultRow={emptyAmcBoqRow}
                          embedded
                        />
                      </div>
                    </CardBody>
                    <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                      <BackButton />
                      <Button
                        type="submit"
                        color="primary"
                        className="btn-label px-4"
                        disabled={
                          isCancelled || !canSubmit || validation.isSubmitting
                        }
                      >
                        {validation.isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            />
                            {isEdit ? "Updating…" : "Submitting…"}
                          </>
                        ) : isEdit ? (
                          "Save changes"
                        ) : (
                          "Create contract"
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                </Col>
              </Row>
            </Form>
          </FormikProvider>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ContractForm;
